import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { ScrollArea } from '@neo/primitives/scroll-area'
import type { SelectOption } from '@neo/primitives/select'
import { useMemo, useRef, useState } from 'react'
import { useProfileQuery, useTimezonesQuery } from 'v2/pages/settings/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import type { AgentSettingsFormValues } from '../config/base.config'
import { useAgentSettingsVisibility } from '../hooks/use-agent-settings-visibility'
import { useFlowId } from '../hooks/use-flow-id'
import { useFlowQuery } from '../queries'
import { agentSettingsFields } from '../sidebar/agent-settings/agent-settings.config'
import { useFlowStore } from '../store'
import { agentDefaultTimezone } from '../utils/agent-default-timezone'
import { AgentPhoneNumbers } from './agent-phone-numbers'
import { phoneNumbersAgentId } from './agent-phone-numbers.utils'
import { AgentSubaccountControl } from './agent-subaccount-control'
import { useIsOutboundFlow } from './agent-subaccount-warnings'

export const AgentSettingsTab = () => {
  const formRef = useRef<GenericFormType<AgentSettingsFormValues>>()
  const agentSettings = useFlowStore((state) => state.agentSettings)
  // Same edit-mode lock the canvas and node sidebar use, so Settings follows the
  // Edit/Publish buttons instead of staying live in view mode.
  const isEditing = useFlowStore((state) => state.isEditing)
  const { showOperatingHours } = useAgentSettingsVisibility()
  const { id: flowId, isNew } = useFlowId()
  // Pass the real isNew: a ?new unsaved agent must skip the BE fetch (isNew
  // returns the blank canvas) — hardcoding false made Settings 404 -> NotFound.
  const { data: flowData } = useFlowQuery(flowId || '', undefined, isNew)
  const currentSubAuthId = flowData?.phlo?.sub_auth_id ?? null
  const { subaccountTaggingEnabled } = useFeatureFlags()
  const isOutboundFlow = useIsOutboundFlow()
  const currentFlowType = useFlowStore((state) => state.currentFlowType)
  const { hasPermission } = usePermissions()
  const numbersAgentId = phoneNumbersAgentId({
    embed: isEmbedMode(),
    flowId,
    isNew,
    flowType: currentFlowType,
  })

  const { data: profileData } = useProfileQuery()
  const [isFormReady, setIsFormReady] = useState(false)
  const { data: timezones } = useTimezonesQuery((data) =>
    data?.timezone_list
      ? (data.timezone_list.map((timezone) => ({
          label: timezone.name,
          value: timezone.tz_value,
        })) as SelectOption[])
      : [],
  )

  const formFields = useMemo(() => {
    return agentSettingsFields([], [], undefined, 'us', timezones || [])
  }, [timezones])

  // Same fallback as save: embed has no profile, so the Timezone select would be empty.
  const orgTimezone = agentDefaultTimezone(profileData?.organizations?.[0]?.timezone)

  const defaultValues = useMemo(() => {
    if (!agentSettings) return null

    if (agentSettings.schedule === null && orgTimezone) {
      return {
        ...agentSettings,
        schedule: {
          timezone: orgTimezone,
          start_time: '',
          end_time: '',
        },
      }
    }

    return agentSettings
  }, [agentSettings, orgTimezone])

  const getScheduleFields = () =>
    formFields
      .filter((field) =>
        showOperatingHours ? field.name.startsWith('schedule') : field.name === 'schedule.timezone',
      )
      .map((field) => {
        const fieldDisabled = (field as { disabled?: (state: unknown) => boolean | string })
          .disabled
        return {
          ...field,
          // Composes with a field's own rule rather than replacing it, matching
          // sidebar.base.tsx (a string result keeps its tooltip semantics).
          disabled: (state: unknown) =>
            !isEditing || (fieldDisabled ? fieldDisabled(state) : false),
        }
      }) as GenericFieldConfig<AgentSettingsFormValues>

  return (
    <div className="h-full">
      <ScrollArea className="h-full">
        <div className="w-full max-w-3xl overflow-visible pb-6">
          {defaultValues && (
            <GenericForm<AgentSettingsFormValues>
              id="agent-settings-tab-form"
              fields={[]}
              onMount={(form: GenericFormType<AgentSettingsFormValues>) => {
                formRef.current = form
                setIsFormReady(true)
                useFlowStore.getState().setTabFormInstance('settings', form)
                return () => useFlowStore.getState().setTabFormInstance('settings', null)
              }}
              onSubmit={() => {}}
              includeSubmitButton={false}
              defaultValues={defaultValues}
              className="gap-0"
            >
              <Accordion type="multiple" defaultValue={['operating-hours']}>
                <AccordionItem
                  value="operating-hours"
                  className="rounded-lg border border-border bg-card px-4"
                >
                  <AccordionTrigger className="rounded-md py-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2">
                    <div className="text-left">
                      <div className="text-p-600 text-foreground">
                        {showOperatingHours ? 'Timezone & operating hours' : 'Timezone'}
                      </div>
                      <div className="mt-1 max-w-2xl text-s-400 text-secondary">
                        {showOperatingHours
                          ? 'Set the timezone and optionally restrict the hours this agent accepts requests. Leave times blank to run anytime.'
                          : 'Used by the agent when referring to dates and times. Defaults to your account timezone.'}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-4 border-t border-border pt-4 md:grid-cols-2">
                      {isFormReady && formRef.current && (
                        <FieldsRenderer fields={getScheduleFields()} form={formRef.current} />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              {/* Deliberately NOT an AccordionItem: this is always expanded because a
                  collapsed section hides which account the agent bills to. The classes
                  mirror the accordion items so it still reads as part of the same list.
                  The per-org flag gate lives here rather than inside the control so the
                  section heading disappears with it. Hiding is safe for an already-tagged
                  flow: the save carries the tag only when it was changed here, so an
                  unrelated save leaves the server's tag alone, which also covers
                  outbound flows, where tagging is not offered. Never in embed: the
                  sub-account list is not allow-listed there. */}
              {flowId && subaccountTaggingEnabled && !isEmbedMode() && !isOutboundFlow && (
                <div className="border-b" data-testid="section-agent-subaccount">
                  <div className="py-4 text-left">
                    <div className="text-p-600">Sub account</div>
                    <div className="text-s-400 text-muted-foreground">
                      Run and bill this agent under a Plivo sub account.
                    </div>
                  </div>
                  <div className="pb-4 pt-0">
                    <AgentSubaccountControl
                      currentSubAuthId={currentSubAuthId}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              )}
            </GenericForm>
          )}
          {/* Outside the form: it saves on its own, not with the agent. */}
          {numbersAgentId && (
            <AgentPhoneNumbers
              agentId={numbersAgentId}
              savedAgent={flowData?.phlo}
              canEdit={hasPermission(RbacModules.aiAgent, RbacPermissionTypes.edit)}
            />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
